import { useEffect, useMemo, useRef, useState } from "react";
import { Input } from "./ui/input";
import { Search, X } from "lucide-react";

/**
 * Predictive picker over a local list (suppliers, equipment, approvers…) with debounced filtering.
 * options: [{ value, label, hint? }]
 */
export function SearchSelect({ value, options, onChange, placeholder = "Buscar…", testid, emptyLabel = "—", disabled, className = "" }) {
  const selected = useMemo(() => options.find((o) => o.value === value), [options, value]);
  const [q, setQ] = useState(selected?.label || "");
  const [debounced, setDebounced] = useState("");
  const [open, setOpen] = useState(false);
  const [idx, setIdx] = useState(0);
  const box = useRef(null);

  useEffect(() => { setQ(selected?.label || ""); }, [selected]);
  useEffect(() => { const h = setTimeout(() => setDebounced(q), 180); return () => clearTimeout(h); }, [q]);
  useEffect(() => {
    const onClick = (e) => { if (box.current && !box.current.contains(e.target)) { setOpen(false); setQ(selected?.label || ""); } };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [selected]);

  const results = useMemo(() => {
    const s = debounced.trim().toLowerCase();
    const list = !s || debounced === selected?.label ? options : options.filter((o) => `${o.label} ${o.hint || ""}`.toLowerCase().includes(s));
    return list.slice(0, 40);
  }, [debounced, options, selected]);

  const pick = (o) => { onChange(o ? o.value : "", o); setQ(o?.label || ""); setOpen(false); };
  const onKey = (e) => {
    if (!open) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setIdx((i) => Math.min(i + 1, results.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setIdx((i) => Math.max(i - 1, 0)); }
    else if (e.key === "Enter") { e.preventDefault(); if (results[idx]) pick(results[idx]); }
    else if (e.key === "Escape") setOpen(false);
  };

  return (
    <div className={`relative ${className}`} ref={box} data-testid={testid}>
      <Search size={11} className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
      <Input value={q} disabled={disabled} placeholder={placeholder}
        onChange={(e) => { setQ(e.target.value); setOpen(true); setIdx(0); }}
        onFocus={() => setOpen(true)} onKeyDown={onKey}
        className="rounded-none h-8 text-xs pl-6 pr-6" data-testid={`${testid}-input`} />
      {value && !disabled && (
        <button type="button" onClick={() => pick(null)} className="absolute right-1.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-destructive" data-testid={`${testid}-clear`}><X size={12} /></button>
      )}
      {open && (
        <div className="absolute top-full left-0 right-0 z-50 mt-0.5 border bg-popover shadow-lg max-h-64 overflow-y-auto" data-testid={`${testid}-results`}>
          <button type="button" onClick={() => pick(null)} className="w-full text-left px-3 py-1.5 text-[11px] text-muted-foreground border-b hover:bg-muted">{emptyLabel}</button>
          {results.length === 0 && <div className="p-3 text-[11px] text-muted-foreground text-center">Sin resultados</div>}
          {results.map((o, i) => (
            <button key={o.value} type="button" onClick={() => pick(o)} onMouseEnter={() => setIdx(i)}
              className={`w-full text-left px-3 py-1.5 border-b last:border-b-0 hover:bg-muted ${i === idx ? "bg-muted" : ""}`}
              data-testid={`${testid}-option-${o.value}`}>
              <div className="text-xs font-bold">{o.label}</div>
              {o.hint && <div className="text-[10px] text-muted-foreground truncate">{o.hint}</div>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
